import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { DollarSign, Users, TrendingUp, Activity } from 'lucide-react';

interface AnalyticsData {
  totalSubscriptions: number;
  activeSubscriptions: number;
  totalJobs: number;
  completedJobs: number;
  failedJobs: number;
  planBreakdown: {
    free: number;
    starter: number;
    pro: number;
  };
}

interface RecentJob {
  id: string;
  email: string;
  status: string;
  created_at: string;
  completed_at: string | null;
}

export default function RevenueAnalytics() {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    totalJobs: 0,
    completedJobs: 0,
    failedJobs: 0,
    planBreakdown: {
      free: 0,
      starter: 0,
      pro: 0,
    },
  });
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [subsRes, jobsRes, completedRes, failedRes, freeRes, starterRes, proRes, recentRes] = await Promise.all([
        supabase.from('wordpress_subscriptions').select('id', { count: 'exact', head: true }),
        supabase.from('wordpress_jobs').select('id', { count: 'exact', head: true }),
        supabase.from('wordpress_jobs').select('id', { count: 'exact', head: true }).eq('status', 'completed'),
        supabase.from('wordpress_jobs').select('id', { count: 'exact', head: true }).eq('status', 'failed'),
        supabase.from('wordpress_subscriptions').select('id', { count: 'exact', head: true }).eq('plan', 'free'),
        supabase.from('wordpress_subscriptions').select('id', { count: 'exact', head: true }).eq('plan', 'starter'),
        supabase.from('wordpress_subscriptions').select('id', { count: 'exact', head: true }).eq('plan', 'pro'),
        supabase.from('wordpress_jobs').select('id, email, status, created_at, completed_at').order('created_at', { ascending: false }).limit(10),
      ]);

      setAnalytics({
        totalSubscriptions: subsRes.count || 0,
        activeSubscriptions: (starterRes.count || 0) + (proRes.count || 0),
        totalJobs: jobsRes.count || 0,
        completedJobs: completedRes.count || 0,
        failedJobs: failedRes.count || 0,
        planBreakdown: {
          free: freeRes.count || 0,
          starter: starterRes.count || 0,
          pro: proRes.count || 0,
        },
      });

      setRecentJobs(recentRes.data || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Revenue Analytics</h1>
          <p className="mt-2 text-gray-600">Track subscriptions and usage metrics</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Subscriptions</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.totalSubscriptions}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Paid</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.activeSubscriptions}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Jobs</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.totalJobs}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <Activity className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed Jobs</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{analytics.completedJobs}</p>
              </div>
              <div className="bg-teal-100 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Plan Distribution</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  <span className="text-gray-700">Free Plan</span>
                </div>
                <span className="font-semibold text-gray-900">{analytics.planBreakdown.free}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-gray-700">Starter Plan</span>
                </div>
                <span className="font-semibold text-gray-900">{analytics.planBreakdown.starter}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-gray-700">Pro Plan</span>
                </div>
                <span className="font-semibold text-gray-900">{analytics.planBreakdown.pro}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Job Statistics</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Success Rate</span>
                <span className="font-semibold text-green-600">
                  {analytics.totalJobs > 0
                    ? Math.round((analytics.completedJobs / analytics.totalJobs) * 100)
                    : 0}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Failed Jobs</span>
                <span className="font-semibold text-red-600">{analytics.failedJobs}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Processing</span>
                <span className="font-semibold text-blue-600">
                  {analytics.totalJobs - analytics.completedJobs - analytics.failedJobs}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Recent Jobs</h2>
          </div>
          {recentJobs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Jobs will appear here once users start optimizing resumes.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentJobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {job.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(job.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {job.completed_at ? new Date(job.completed_at).toLocaleString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
